import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, LifeBuoy, Timer, UserX, Wrench } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";

export const fmtDuration = (h) => (h == null ? "—" : h < 48 ? `${Math.round(h)} h` : `${(h / 24).toFixed(1).replace(".", ",")} j`);
const PERIODS = [["today", "Aujourd'hui"], ["30d", "30 jours"], ["90d", "90 jours"], ["all", "Tout"]];

function Kpi({ icon: Icon, label, value, sub, tone, to, id }) {
  const body = (
    <div className={`p-4 rounded-md border bg-white h-full transition-colors ${to ? "hover:border-norea-primary/50" : ""} ${tone || "border-norea-border"}`} data-testid={`sav-kpi-${id}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-norea-textMuted"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-3xl font-heading font-medium tabular-nums mt-1" data-testid={`sav-kpi-${id}-value`}>{value ?? "…"}</div>
      {sub && <div className="text-[11px] text-norea-textMuted mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

function Breakdown({ title, rows, testid, showSolve }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <section className="bg-white border border-norea-border rounded-md p-4" data-testid={testid}>
      <h3 className="font-heading text-base font-medium mb-3">{title}</h3>
      {rows.length === 0 ? <div className="text-sm italic text-norea-textMuted py-4 text-center">Aucune demande sur la période.</div> : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-norea-textMuted"><tr><th className="text-left font-semibold pb-1.5"></th><th className="text-right font-semibold">Total</th>
            <th className="text-right font-semibold">Ouverts</th><th className="text-right font-semibold">En retard</th>{showSolve && <th className="text-right font-semibold">Résolution moy.</th>}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-norea-border" data-testid={`${testid}-row-${r.id}`}>
                <td className="py-1.5 pr-3 w-1/2"><div className="text-xs">{r.label}</div>
                  <div className="h-1.5 mt-1 rounded-full bg-norea-bg overflow-hidden"><div className="h-full bg-norea-primary rounded-full" style={{ width: `${(r.total / max) * 100}%` }} /></div></td>
                <td className="text-right tabular-nums">{r.total}</td><td className="text-right tabular-nums">{r.open}</td>
                <td className={`text-right tabular-nums ${r.late ? "text-amber-700 font-medium" : ""}`}>{r.late}</td>
                {showSolve && <td className="text-right text-xs">{fmtDuration(r.avg_resolution_h)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function SavDashboardPage() {
  const [period, setPeriod] = useState("30d");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/sav/stats", { params: { period } }).then((r) => setD(r.data)).catch(() => setD({})); }, [period]);
  return (
    <AppLayout>
      <div className="space-y-5" data-testid="sav-dashboard-page">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-xs text-norea-textMuted"><Link to="/sav" className="hover:underline">Service Après-Vente</Link></div>
            <h1 className="font-heading text-3xl font-medium flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-norea-primary" />Tableau de bord SAV</h1>
          </div>
          <div className="inline-flex bg-white border border-norea-border rounded-md p-0.5">
            {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} className={`px-2.5 py-1 text-xs rounded transition-colors ${period === k ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`sav-period-${k}`}>{l}</button>)}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi id="open" icon={Wrench} label="SAV ouverts" value={d?.open} sub="tous les SAV non finis" to="/sav?vue=ouverts" />
          <Kpi id="late" icon={AlertTriangle} label="En retard" value={d?.late} sub={d ? `${d.late_detail?.relance2 ?? 0} en relance 2 · ${d.late_detail?.relance3 ?? 0} en relance 3` : ""} tone={d?.late ? "border-amber-300" : ""} to="/sav?vue=en_retard" />
          <Kpi id="reassign" icon={UserX} label="À réattribuer" value={d?.to_reassign} sub="3 relances sans réponse" tone={d?.to_reassign ? "border-red-300" : ""} to="/sav?vue=a_reattribuer" />
          <Kpi id="takeover" icon={Clock} label="Prise en charge moyenne" value={fmtDuration(d?.avg_takeover_h)} sub={`ouverture → En cours ou RDV fixé (${d?.takeover_n ?? 0} SAV)`} />
          <Kpi id="resolution" icon={Timer} label="Résolution moyenne" value={fmtDuration(d?.avg_resolution_h)} sub={`ouverture → Fini (${d?.resolution_n ?? 0} SAV)`} />
        </div>
        <div className="text-[11px] text-norea-textMuted -mt-2">Ouverts, en retard et à réattribuer : situation actuelle. Délais et répartitions : SAV ouverts sur la période ({d?.created ?? 0} demande(s), {d?.done ?? 0} finie(s)).</div>
        <section className="bg-white border border-norea-border rounded-md p-4" data-testid="sav-by-status">
          <h3 className="font-heading text-base font-medium mb-2">Par statut (actuel)</h3>
          <div className="flex flex-wrap gap-2">{(d?.by_status || []).map((s) => <span key={s.id} className="text-xs px-2 py-1 rounded border border-norea-border" data-testid={`sav-status-count-${s.id}`}>{s.label} <strong className="tabular-nums">{s.count}</strong></span>)}</div>
        </section>
        <div className="grid md:grid-cols-2 gap-4">
          <Breakdown title="Par sous-traitant" rows={d?.by_subcontractor || []} testid="sav-by-subcontractor" showSolve />
          <Breakdown title="Par type de matériel" rows={d?.by_materiel || []} testid="sav-by-materiel" showSolve />
        </div>
      </div>
    </AppLayout>
  );
}
